import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Button, Loading } from '../components/ui';
import { useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface MediaItem {
  id: number;
  filename: string;
  url: string;
  type: string;
  size: number;
  uploaded_at: string;
}

const MediaLibraryPage: React.FC = () => {
  const media = useCrud<MediaItem>('/api/media');
  const { showToast } = useToast();
  const [filter, setFilter] = useState<'all' | 'image' | 'audio'>('all');

  useEffect(() => { media.fetchAll(); }, []);

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this file? This cannot be undone.')) {
      const result = await media.remove(id);
      if (result) showToast('File deleted', 'success');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredMedia = media.items.filter(item => {
    if (filter === 'all') return true;
    return item.type === filter;
  });

  if (media.loading && media.items.length === 0) return <Loading />;

  return (
    <>
      <PageHeader
        title="Media Library"
        subtitle="View and manage uploaded files"
      />

      <div className="admin-content">
        <Card>
          <div style={{ marginBottom: 20, display: 'flex', gap: 8 }}>
            <Button
              size="sm"
              variant={filter === 'all' ? 'primary' : 'secondary'}
              onClick={() => setFilter('all')}
            >
              All ({media.items.length})
            </Button>
            <Button
              size="sm"
              variant={filter === 'image' ? 'primary' : 'secondary'}
              onClick={() => setFilter('image')}
            >
              Images ({media.items.filter(i => i.type === 'image').length})
            </Button>
            <Button
              size="sm"
              variant={filter === 'audio' ? 'primary' : 'secondary'}
              onClick={() => setFilter('audio')}
            >
              Audio ({media.items.filter(i => i.type === 'audio').length})
            </Button>
          </div>

          {filteredMedia.length === 0 ? (
            <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 40 }}>
              {filter === 'all'
                ? 'No files uploaded yet. Upload files through content forms.'
                : `No ${filter} files found.`}
            </p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 16
            }}>
              {filteredMedia.map(item => (
                <div
                  key={item.id}
                  style={{
                    background: 'var(--admin-bg)',
                    border: '1px solid var(--admin-border)',
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.2)',
                  }}>
                    {item.type === 'image' ? (
                      <img
                        src={item.url}
                        alt={item.filename}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: '2rem' }}>🎵</span>
                    )}
                  </div>
                  <div style={{ padding: 12 }}>
                    <div style={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: 4
                    }}>
                      {item.filename}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: 8 }}>
                      {formatSize(item.size)} • {new Date(item.uploaded_at).toLocaleDateString()}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigator.clipboard.writeText(item.url).then(() => showToast('URL copied!', 'success'))}
                      >
                        Copy URL
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export default MediaLibraryPage;
